// pages/CI/SLDEditor.js
//
// CI-admin SLD authoring tool — port of the Mideast MESites diagram editor,
// pointed at the MAIN commercial-sld module (/api/commercial-sld/*) and the main
// auth token. Flow: pick Client → pick Site → upload diagram → place pins
// (nodes) and draw strings (polygons). This seeds what the merged field app
// captures against and the CI viewer renders.
//
// Auth note: /commercial-clients and /commercial-sites are unauthenticated in
// this backend, but /commercial-sld/* require a Bearer token (admin for writes),
// so those calls attach the main `token`.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, X, Upload, Trash2, CheckCircle2, Move, BoxSelect, Layers, MapPin,
} from 'lucide-react';
import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE_URL;
const IMG_BASE = BASE ? BASE.replace('/api', '') : '';
const authHdr = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});
const multipartHdr = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'multipart/form-data',
  },
});

const STRING_COLORS = [
  '#3B82F6', '#22D3A5', '#F59E0B', '#EF4444',
  '#A855F7', '#EC4899', '#F97316', '#14B8A6',
];

const centroid = (pts) => ({
  x: pts.reduce((s, p) => s + Number(p.x), 0) / pts.length,
  y: pts.reduce((s, p) => s + Number(p.y), 0) / pts.length,
});
const svgPts = (pts) => pts.map((p) => `${Number(p.x)},${Number(p.y)}`).join(' ');

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=DM+Mono:wght@400;500&display=swap');

  .csld-root { font-family: 'DM Sans', sans-serif; color: #E2E8F0; padding: 4px; }
  .md-page-title { font-size: 22px; font-weight: 800; color: #F1F5F9; }
  .md-page-sub   { font-size: 12px; color: rgba(255,255,255,0.35); margin-top: 3px; font-family: 'DM Mono', monospace; }

  .md-btn {
    display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px;
    border-radius: 9px; font-size: 13px; font-weight: 700; font-family: 'DM Sans', sans-serif;
    cursor: pointer; border: 1px solid transparent; transition: all 0.15s;
  }
  .md-btn:disabled { opacity: 0.55; cursor: not-allowed; }
  .md-btn-primary { background: #0EA5E9; color: #04121f; }
  .md-btn-primary:hover:not(:disabled) { background: #38BDF8; }
  .md-btn-ghost { background: rgba(255,255,255,0.05); color: #E2E8F0; border-color: rgba(255,255,255,0.10); }
  .md-btn-ghost:hover:not(:disabled) { background: rgba(255,255,255,0.09); }
  .md-btn-danger { background: rgba(239,68,68,0.12); color: #FCA5A5; border-color: rgba(239,68,68,0.25); }
  .md-btn-danger:hover:not(:disabled) { background: rgba(239,68,68,0.20); }

  .md-spinner {
    display: inline-block; width: 13px; height: 13px;
    border: 2px solid rgba(255,255,255,0.25); border-top-color: #fff;
    border-radius: 50%; animation: md-spin 0.7s linear infinite;
  }
  @keyframes md-spin { to { transform: rotate(360deg); } }
  .md-skeleton {
    background: linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.08), rgba(255,255,255,0.04));
    background-size: 200% 100%; animation: md-shimmer 1.3s infinite; border-radius: 12px;
  }
  @keyframes md-shimmer { to { background-position: -200% 0; } }
  .md-empty { text-align: center; color: rgba(255,255,255,0.35); font-size: 13px; padding: 36px 0; }
  .md-empty-icon {
    width: 48px; height: 48px; border-radius: 14px; background: rgba(255,255,255,0.04);
    display: flex; align-items: center; justify-content: center; margin: 0 auto 12px; color: rgba(255,255,255,0.30);
  }

  .csld-card {
    background: #0D1120; border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px; padding: 18px; margin-bottom: 18px;
  }
  .csld-pickers { display: flex; gap: 14px; flex-wrap: wrap; }
  .csld-field { display: flex; flex-direction: column; gap: 6px; min-width: 240px; flex: 1; }
  .csld-label {
    font-size: 9px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase;
    color: rgba(255,255,255,0.30); font-family: 'DM Mono', monospace;
  }
  .csld-select {
    padding: 10px 12px; background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.10); border-radius: 10px;
    color: #E2E8F0; font-size: 13px; font-family: 'DM Sans', sans-serif; outline: none; cursor: pointer;
  }
  .csld-select option { background: #1E2535; }

  /* Diagram upload zone */
  .ms-diagram-upload-zone {
    border: 2px dashed rgba(14,165,233,0.25); border-radius: 14px; padding: 40px 24px;
    text-align: center; cursor: pointer; transition: all 0.2s; background: rgba(14,165,233,0.03);
  }
  .ms-diagram-upload-zone:hover { border-color: rgba(14,165,233,0.50); background: rgba(14,165,233,0.06); }
  .ms-diagram-upload-icon {
    width: 52px; height: 52px; border-radius: 14px; background: rgba(14,165,233,0.10);
    display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; color: #38BDF8;
  }
  .ms-diagram-upload-title { font-size: 14px; font-weight: 700; color: #E2E8F0; margin-bottom: 6px; }
  .ms-diagram-upload-sub   { font-size: 12px; color: rgba(255,255,255,0.28); }

  .ms-mode-toggle { display: flex; gap: 6px; margin-bottom: 14px; }
  .ms-mode-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 8px;
    font-size: 11px; font-weight: 700; font-family: 'DM Mono', monospace; cursor: pointer;
    border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.35); transition: all 0.18s;
  }
  .ms-mode-btn.active-pins    { background: rgba(245,158,11,0.12); color: #FCD34D; border-color: rgba(245,158,11,0.28); }
  .ms-mode-btn.active-strings { background: rgba(59,130,246,0.12); color: #93C5FD; border-color: rgba(59,130,246,0.28); }

  .ms-pin-canvas-wrap {
    position: relative; display: inline-block; width: 100%; border-radius: 10px; overflow: hidden;
    border: 1px solid rgba(14,165,233,0.20); user-select: none; cursor: crosshair;
  }
  .ms-pin-canvas-img { width: 100%; display: block; user-select: none; -webkit-user-drag: none; }
  .ms-svg-overlay { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; overflow: visible; }
  .ms-string-label-chip {
    position: absolute; transform: translate(-50%, -50%); font-size: 10px; font-weight: 800;
    font-family: 'DM Mono', monospace; border-radius: 4px; padding: 2px 7px; white-space: nowrap;
    background: rgba(0,0,0,0.70); pointer-events: none; z-index: 10;
  }
  .ms-draft-toolbar {
    display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 10px; margin-bottom: 10px;
    background: rgba(59,130,246,0.08); border: 1px solid rgba(59,130,246,0.20);
    font-size: 11px; font-weight: 700; font-family: 'DM Mono', monospace; color: rgba(147,197,253,0.75);
  }
  .ms-pin { position: absolute; transform: translate(-50%, -50%); z-index: 10; cursor: pointer; }
  .ms-pin-dot {
    width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    font-size: 9px; font-weight: 800; color: #080C18; background: #F59E0B;
    box-shadow: 0 0 0 3px rgba(245,158,11,0.30), 0 4px 12px rgba(0,0,0,0.50);
    transition: transform 0.15s; font-family: 'DM Mono', monospace;
  }
  .ms-pin:hover .ms-pin-dot { transform: scale(1.2); }
  .ms-pin-tooltip {
    position: absolute; bottom: 28px; left: 50%; transform: translateX(-50%);
    background: #0D1829; border: 1px solid rgba(14,165,233,0.25); border-radius: 7px; padding: 5px 10px;
    white-space: nowrap; font-size: 11px; color: #E2E8F0; font-weight: 600; pointer-events: none;
    opacity: 0; transition: opacity 0.15s; box-shadow: 0 4px 14px rgba(0,0,0,0.40); z-index: 20;
  }
  .ms-pin:hover .ms-pin-tooltip { opacity: 1; }
  .ms-pin-remove {
    position: absolute; top: -6px; right: -6px; width: 14px; height: 14px; border-radius: 50%;
    background: #EF4444; color: #fff; display: flex; align-items: center; justify-content: center;
    font-size: 8px; opacity: 0; transition: opacity 0.15s; cursor: pointer; border: none;
  }
  .ms-pin:hover .ms-pin-remove { opacity: 1; }
  .ms-pin-list, .ms-string-list { display: flex; flex-direction: column; gap: 8px; margin-top: 16px; }
  .ms-pin-list-item, .ms-string-item {
    display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 9px;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06);
  }
  .ms-pin-list-num {
    width: 22px; height: 22px; border-radius: 50%; background: #F59E0B; color: #080C18;
    display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 800;
    flex-shrink: 0; font-family: 'DM Mono', monospace;
  }
  .ms-pin-label-input {
    flex: 1; background: transparent; border: none; outline: none; color: #E2E8F0; font-size: 13px;
    border-bottom: 1px solid rgba(255,255,255,0.08); padding: 2px 0;
  }
  .ms-pin-label-input:focus { border-bottom-color: rgba(14,165,233,0.40); }
  .ms-string-color-block { width: 14px; height: 14px; border-radius: 3px; flex-shrink: 0; }
  .ms-pending-form {
    background: rgba(59,130,246,0.06); border: 1px solid rgba(59,130,246,0.22);
    border-radius: 12px; padding: 16px; margin-top: 14px;
  }
  .ms-pending-form-title {
    font-size: 9px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase;
    color: rgba(147,197,253,0.65); font-family: 'DM Mono', monospace; margin-bottom: 12px;
  }
  .ms-color-row { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; }
  .ms-color-swatch {
    width: 22px; height: 22px; border-radius: 50%; cursor: pointer; flex-shrink: 0;
    border: 2px solid transparent; transition: transform 0.15s, border-color 0.15s;
  }
  .ms-color-swatch:hover { transform: scale(1.25); }
  .ms-color-swatch.selected { border-color: rgba(255,255,255,0.75); transform: scale(1.15); }
  .ms-canvas-hint {
    font-size: 11px; color: rgba(255,255,255,0.28); font-family: 'DM Mono', monospace;
    text-align: center; margin: 10px 0 16px; display: flex; align-items: center; justify-content: center; gap: 6px;
  }
  .csld-input {
    width: 100%; padding: 9px 12px; background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.10); border-radius: 9px; color: #E2E8F0;
    font-size: 13px; font-family: 'DM Sans', sans-serif; outline: none;
  }
`;

const CISLDEditor = () => {
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [loadingSites, setLoadingSites] = useState(false);

  const [diagram, setDiagram] = useState(null);
  const [diagramLoading, setDiagramLoading] = useState(false);
  const [pins, setPins] = useState([]);
  const [strings, setStrings] = useState([]);
  const [savingPins, setSavingPins] = useState(false);
  const [uploadingDiag, setUploadingDiag] = useState(false);

  const [diagramMode, setDiagramMode] = useState('pins');
  const [draftPoints, setDraftPoints] = useState([]);
  const [mousePos, setMousePos] = useState(null);
  const [pendingShape, setPendingShape] = useState(null);
  const [pendingLabel, setPendingLabel] = useState('');
  const [pendingColor, setPendingColor] = useState(STRING_COLORS[0]);
  const [savingString, setSavingString] = useState(false);

  const diagramInputRef = useRef(null);
  const canvasRef = useRef(null);

  const selectedSite = sites.find((s) => String(s.id) === String(selectedSiteId)) || null;

  // ── Clients ───────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BASE}/commercial-clients`);
        if (r.data?.success) setClients(r.data.data.clients || []);
      } catch (e) {
        console.error('[CI SLD] load clients error:', e);
      }
    })();
  }, []);

  // ── Sites for the chosen client ───────────────────────────────────
  useEffect(() => {
    if (!selectedClientId) { setSites([]); setSelectedSiteId(''); return; }
    (async () => {
      setLoadingSites(true);
      try {
        const r = await axios.get(`${BASE}/commercial-sites/${selectedClientId}`);
        setSites(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        console.error('[CI SLD] load sites error:', e);
        setSites([]);
      } finally {
        setLoadingSites(false);
      }
    })();
    setSelectedSiteId('');
    setDiagram(null);
    setPins([]);
    setStrings([]);
  }, [selectedClientId]);

  const resetDraft = () => { setDraftPoints([]); setMousePos(null); setPendingShape(null); setPendingLabel(''); setPendingColor(STRING_COLORS[0]); };

  // ── Diagram for the chosen site ───────────────────────────────────
  const loadDiagram = useCallback(async (siteId) => {
    setDiagramLoading(true);
    setDiagram(null); setPins([]); setStrings([]); setDiagramMode('pins'); resetDraft();
    try {
      const listRes = await axios.get(`${BASE}/commercial-sld/diagrams/site/${siteId}`, authHdr());
      const list = listRes.data?.diagrams || [];
      if (!list.length) { setDiagram(null); return; }
      const id = list[0].id;
      const [dRes, sRes] = await Promise.all([
        axios.get(`${BASE}/commercial-sld/diagrams/${id}`, authHdr()),
        axios.get(`${BASE}/commercial-sld/strings/diagram/${id}`, authHdr()).catch(() => ({ data: { strings: [] } })),
      ]);
      const d = dRes.data.diagram;
      setDiagram(d);
      setPins((d.points || []).map((p, i) => ({ ...p, _localId: p.id ?? `n${i}` })));
      setStrings(sRes.data.strings || []);
    } catch (e) {
      console.error('[CI SLD] load diagram error:', e);
    } finally {
      setDiagramLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedSiteId) loadDiagram(selectedSiteId);
  }, [selectedSiteId, loadDiagram]);

  // ── Canvas helpers ────────────────────────────────────────────────
  const getCursorPct = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: parseFloat(((e.clientX - rect.left) / rect.width * 100).toFixed(2)),
      y: parseFloat(((e.clientY - rect.top) / rect.height * 100).toFixed(2)),
    };
  };

  const handleCanvasClick = (e) => {
    if (!canvasRef.current) return;
    const { x, y } = getCursorPct(e);
    if (diagramMode === 'pins') {
      setPins((prev) => [...prev, { _localId: Date.now(), label: '', x_percent: x, y_percent: y, order_index: prev.length }]);
      return;
    }
    if (diagramMode === 'strings') {
      if (pendingShape) return;
      if (draftPoints.length >= 3) {
        const first = draftPoints[0];
        const dist = Math.sqrt((x - first.x) ** 2 + (y - first.y) ** 2);
        if (dist < 4) { setPendingShape([...draftPoints]); setDraftPoints([]); setMousePos(null); return; }
      }
      setDraftPoints((prev) => [...prev, { x, y }]);
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (diagramMode !== 'strings' || draftPoints.length === 0 || pendingShape || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setMousePos({ x: (e.clientX - rect.left) / rect.width * 100, y: (e.clientY - rect.top) / rect.height * 100 });
  };

  const finishShape = () => { if (draftPoints.length < 3) return; setPendingShape([...draftPoints]); setDraftPoints([]); setMousePos(null); };
  const cancelDraft = () => { setDraftPoints([]); setMousePos(null); };
  const cancelPendingShape = () => { setPendingShape(null); setPendingLabel(''); setPendingColor(STRING_COLORS[0]); };

  // ── Strings ───────────────────────────────────────────────────────
  const saveString = async () => {
    if (!pendingLabel.trim() || !pendingShape || !diagram) return;
    setSavingString(true);
    try {
      const res = await axios.post(`${BASE}/commercial-sld/strings`, {
        diagram_id: diagram.id, label: pendingLabel.trim(), color: pendingColor, points: pendingShape,
      }, authHdr());
      setStrings((prev) => [...prev, res.data.string]);
      cancelPendingShape();
    } catch (e) {
      alert('Failed to save string');
    } finally {
      setSavingString(false);
    }
  };
  const deleteString = async (id) => {
    if (!window.confirm('Delete this string?')) return;
    try {
      await axios.delete(`${BASE}/commercial-sld/strings/${id}`, authHdr());
      setStrings((prev) => prev.filter((s) => s.id !== id));
    } catch (e) { alert('Failed to delete string'); }
  };

  // ── Pins ──────────────────────────────────────────────────────────
  const removePin = (localId, e) => { e.stopPropagation(); setPins((prev) => prev.filter((p) => p._localId !== localId)); };
  const updatePin = (localId, value) => { setPins((prev) => prev.map((p) => (p._localId === localId ? { ...p, label: value } : p))); };
  const savePins = async () => {
    if (!diagram) return;
    if (pins.some((p) => !String(p.label || '').trim())) { alert('All pins must have a label'); return; }
    setSavingPins(true);
    try {
      const res = await axios.put(`${BASE}/commercial-sld/diagrams/${diagram.id}/points`, {
        points: pins.map((p, i) => ({ label: p.label, x_percent: Number(p.x_percent), y_percent: Number(p.y_percent), order_index: i })),
      }, authHdr());
      setPins((res.data.points || []).map((p, i) => ({ ...p, _localId: p.id ?? `n${i}` })));
      alert('Pins saved');
    } catch (e) { alert('Failed to save pins'); }
    finally { setSavingPins(false); }
  };

  // ── Diagram upload / delete ───────────────────────────────────────
  const handleDiagramUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSiteId) return;
    setUploadingDiag(true);
    try {
      const fd = new FormData();
      fd.append('diagram', file);
      fd.append('site_id', selectedSiteId);
      fd.append('title', selectedSite?.site_name || '');
      const res = await axios.post(`${BASE}/commercial-sld/diagrams`, fd, multipartHdr());
      setDiagram(res.data.diagram);
      setPins([]); setStrings([]); resetDraft();
    } catch (e2) {
      alert('Failed to upload diagram');
    } finally {
      setUploadingDiag(false);
      if (diagramInputRef.current) diagramInputRef.current.value = '';
    }
  };
  const handleDeleteDiagram = async () => {
    if (!diagram || !window.confirm('Remove this diagram and ALL its pins and strings?')) return;
    try {
      await axios.delete(`${BASE}/commercial-sld/diagrams/${diagram.id}`, authHdr());
      setDiagram(null); setPins([]); setStrings([]); resetDraft();
    } catch (e) { alert('Failed to delete diagram'); }
  };

  const nearFirstPoint = draftPoints.length >= 3 && mousePos
    ? Math.sqrt((mousePos.x - draftPoints[0].x) ** 2 + (mousePos.y - draftPoints[0].y) ** 2) < 4
    : false;

  return (
    <div className="csld-root">
      <style>{STYLES}</style>

      <div style={{ marginBottom: 20 }}>
        <div className="md-page-title">Site Diagrams (SLD)</div>
        <div className="md-page-sub">Author diagrams, pins &amp; strings for commercial sites</div>
      </div>

      {/* Pickers */}
      <div className="csld-card">
        <div className="csld-pickers">
          <div className="csld-field">
            <span className="csld-label">Client</span>
            <select className="csld-select" value={selectedClientId} onChange={(e) => setSelectedClientId(e.target.value)}>
              <option value="">Select client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
            </select>
          </div>
          <div className="csld-field">
            <span className="csld-label">Site</span>
            <select className="csld-select" value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)} disabled={!selectedClientId || loadingSites}>
              <option value="">{loadingSites ? 'Loading…' : 'Select site…'}</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.site_name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Editor */}
      {!selectedSiteId ? (
        <div className="csld-card">
          <div className="md-empty">
            <div className="md-empty-icon"><Layers size={24} /></div>
            Pick a client and site to manage its diagram.
          </div>
        </div>
      ) : (
        <div className="csld-card">
          {diagramLoading ? (
            <div className="md-skeleton" style={{ height: 240 }} />
          ) : !diagram ? (
            <>
              <div className="ms-diagram-upload-zone" onClick={() => diagramInputRef.current?.click()}>
                {uploadingDiag ? (
                  <div><span className="md-spinner" /><div style={{ marginTop: 12, fontSize: 13, color: 'rgba(255,255,255,0.40)' }}>Uploading…</div></div>
                ) : (
                  <>
                    <div className="ms-diagram-upload-icon"><Upload size={22} /></div>
                    <div className="ms-diagram-upload-title">Upload Site Diagram for “{selectedSite?.site_name}”</div>
                    <div className="ms-diagram-upload-sub">PNG, JPG or WEBP — max 25MB</div>
                  </>
                )}
              </div>
              <input ref={diagramInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleDiagramUpload} />
            </>
          ) : (
            <>
              {/* Action row */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                <button className="md-btn md-btn-ghost" style={{ fontSize: 11 }} onClick={() => diagramInputRef.current?.click()}><Upload size={12} /> Replace</button>
                <button className="md-btn md-btn-danger" style={{ fontSize: 11 }} onClick={handleDeleteDiagram}><Trash2 size={12} /> Remove</button>
                {diagramMode === 'pins' && (
                  <button className="md-btn md-btn-primary" style={{ fontSize: 11, marginLeft: 'auto' }} onClick={savePins} disabled={savingPins}>
                    {savingPins ? <><span className="md-spinner" /> Saving…</> : <><CheckCircle2 size={12} /> Save Pins</>}
                  </button>
                )}
              </div>
              <input ref={diagramInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleDiagramUpload} />

              {/* Mode toggle */}
              <div className="ms-mode-toggle">
                <button className={`ms-mode-btn${diagramMode === 'pins' ? ' active-pins' : ''}`} onClick={() => { setDiagramMode('pins'); cancelDraft(); cancelPendingShape(); }}>
                  <Move size={12} /> Pins {pins.length > 0 && <span style={{ opacity: 0.65 }}>({pins.length})</span>}
                </button>
                <button className={`ms-mode-btn${diagramMode === 'strings' ? ' active-strings' : ''}`} onClick={() => setDiagramMode('strings')}>
                  <BoxSelect size={12} /> Strings {strings.length > 0 && <span style={{ opacity: 0.65 }}>({strings.length})</span>}
                </button>
              </div>

              {/* Hint */}
              <div className="ms-canvas-hint">
                {diagramMode === 'pins' && <><Move size={12} /> Click the diagram to place a pin</>}
                {diagramMode === 'strings' && draftPoints.length === 0 && !pendingShape && <><BoxSelect size={12} /> Click to start placing polygon points</>}
                {diagramMode === 'strings' && draftPoints.length > 0 && !pendingShape && <><BoxSelect size={12} /> {draftPoints.length} point{draftPoints.length > 1 ? 's' : ''} — {draftPoints.length >= 3 ? 'click the green dot or Finish to close' : `need ${3 - draftPoints.length} more`}</>}
                {diagramMode === 'strings' && pendingShape && <>Label the shape below</>}
              </div>

              {/* Draft toolbar */}
              {diagramMode === 'strings' && draftPoints.length > 0 && !pendingShape && (
                <div className="ms-draft-toolbar">
                  <span style={{ flex: 1 }}>{draftPoints.length} point{draftPoints.length !== 1 ? 's' : ''} placed</span>
                  {draftPoints.length >= 3 && <button className="md-btn md-btn-primary" style={{ fontSize: 11, padding: '5px 12px' }} onClick={finishShape}><CheckCircle2 size={11} /> Finish Shape</button>}
                  <button className="md-btn md-btn-ghost" style={{ fontSize: 11, padding: '5px 10px' }} onClick={cancelDraft}><X size={11} /> Cancel</button>
                </div>
              )}

              {/* Canvas */}
              <div className="ms-pin-canvas-wrap" ref={canvasRef} onClick={handleCanvasClick} onMouseMove={handleCanvasMouseMove} onMouseLeave={() => setMousePos(null)}>
                <img src={`${IMG_BASE}${diagram.diagram_url}`} alt="Site diagram" className="ms-pin-canvas-img" draggable={false} />

                <svg className="ms-svg-overlay" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {strings.map((s) => {
                    const pts = Array.isArray(s.points) ? s.points : [];
                    if (pts.length < 3) return null;
                    return <polygon key={s.id} points={svgPts(pts)} fill={`${s.color}28`} stroke={s.color} strokeWidth="0.5" strokeOpacity="0.85" />;
                  })}
                  {draftPoints.length >= 2 && <polyline points={svgPts(draftPoints)} fill="none" stroke="rgba(255,255,255,0.60)" strokeWidth="0.45" strokeDasharray="2,1" />}
                  {draftPoints.length > 0 && mousePos && (
                    <line x1={draftPoints[draftPoints.length - 1].x} y1={draftPoints[draftPoints.length - 1].y} x2={mousePos.x} y2={mousePos.y} stroke="rgba(255,255,255,0.35)" strokeWidth="0.4" strokeDasharray="1.5,1" />
                  )}
                  {draftPoints.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r={i === 0 ? '1.4' : '0.75'} fill={i === 0 ? (nearFirstPoint ? '#22D3A5' : '#FFFFFF') : 'rgba(255,255,255,0.80)'} stroke={i === 0 ? '#22D3A5' : 'none'} strokeWidth={i === 0 ? '0.5' : '0'} />
                  ))}
                  {nearFirstPoint && <circle cx={draftPoints[0].x} cy={draftPoints[0].y} r="3" fill="none" stroke="#22D3A5" strokeWidth="0.5" opacity="0.75" />}
                </svg>

                {strings.map((s) => {
                  const pts = Array.isArray(s.points) ? s.points : [];
                  if (pts.length < 3) return null;
                  const c = centroid(pts);
                  return <span key={`lbl-${s.id}`} className="ms-string-label-chip" style={{ left: `${c.x}%`, top: `${c.y}%`, color: s.color }}>{s.label}</span>;
                })}

                {pins.map((pin, idx) => (
                  <div key={pin._localId} className="ms-pin" style={{ left: `${pin.x_percent}%`, top: `${pin.y_percent}%` }} onClick={(e) => e.stopPropagation()}>
                    <div className="ms-pin-dot">{idx + 1}</div>
                    <div className="ms-pin-tooltip">{pin.label || `Pin ${idx + 1}`}</div>
                    {diagramMode === 'pins' && <button className="ms-pin-remove" onClick={(e) => removePin(pin._localId, e)}>×</button>}
                  </div>
                ))}
              </div>

              {/* Pending string label form */}
              {diagramMode === 'strings' && pendingShape && (
                <div className="ms-pending-form">
                  <div className="ms-pending-form-title">New String — {pendingShape.length} Points</div>
                  <input className="csld-input" placeholder='Label (e.g. "String 1", "South Array")' value={pendingLabel} onChange={(e) => setPendingLabel(e.target.value)} style={{ marginBottom: 12 }} autoFocus onKeyDown={(e) => { if (e.key === 'Enter') saveString(); if (e.key === 'Escape') cancelPendingShape(); }} />
                  <div className="ms-color-row">
                    <span style={{ fontSize: 10, fontWeight: 700, fontFamily: 'DM Mono', color: 'rgba(255,255,255,0.30)', letterSpacing: '0.12em', textTransform: 'uppercase', flexShrink: 0 }}>Colour</span>
                    {STRING_COLORS.map((c) => <div key={c} className={`ms-color-swatch${pendingColor === c ? ' selected' : ''}`} style={{ background: c }} onClick={() => setPendingColor(c)} />)}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="md-btn md-btn-ghost" style={{ fontSize: 11 }} onClick={cancelPendingShape}>Cancel</button>
                    <button className="md-btn md-btn-primary" style={{ fontSize: 11 }} onClick={saveString} disabled={savingString || !pendingLabel.trim()}>
                      {savingString ? <><span className="md-spinner" /> Saving…</> : <><Plus size={12} /> Save String</>}
                    </button>
                  </div>
                </div>
              )}

              {/* Strings list */}
              {diagramMode === 'strings' && strings.length > 0 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', fontFamily: 'DM Mono', marginTop: 20, marginBottom: 10 }}>Defined Strings ({strings.length})</div>
                  <div className="ms-string-list">
                    {strings.map((s) => (
                      <div key={s.id} className="ms-string-item">
                        <div className="ms-string-color-block" style={{ background: s.color }} />
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#E2E8F0' }}>{s.label}</span>
                        <span style={{ fontSize: 10, fontFamily: 'DM Mono', color: 'rgba(255,255,255,0.22)' }}>{Array.isArray(s.points) ? `${s.points.length} pts` : ''}</span>
                        <button className="md-btn md-btn-danger" style={{ padding: '4px 7px' }} onClick={() => deleteString(s.id)}><Trash2 size={11} /></button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Pins list */}
              {diagramMode === 'pins' && pins.length > 0 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', fontFamily: 'DM Mono', marginTop: 20, marginBottom: 10 }}>Label Pins ({pins.length})</div>
                  <div className="ms-pin-list">
                    {pins.map((pin, idx) => (
                      <div key={pin._localId} className="ms-pin-list-item">
                        <div className="ms-pin-list-num">{idx + 1}</div>
                        <input className="ms-pin-label-input" placeholder={`Pin ${idx + 1} label`} value={pin.label || ''} onChange={(e) => updatePin(pin._localId, e.target.value)} />
                        <button className="md-btn md-btn-danger" style={{ padding: '4px 7px' }} onClick={(e) => removePin(pin._localId, e)}><X size={11} /></button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {diagramMode === 'pins' && pins.length === 0 && (
                <div className="md-empty" style={{ padding: '18px 0' }}>
                  <div className="md-empty-icon"><MapPin size={20} /></div>
                  No pins yet — click the diagram to place one.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CISLDEditor;
